import { ComposedTheme, createUseStyles } from '@glu/theming';

const styles = ({ buttons: { menuButton, primary, secondary, tertiary } }: ComposedTheme) => ({
  root: {
    display: 'inline-block',
    position: 'relative',
  },
  dropdown: {
    height: 40,
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${menuButton.borderColor}`,
    borderRadius: menuButton.borderRadius,
    '&$disabled': {
      borderColor: secondary.disabledBorder,
    },
    '&$primary': {
      '&$disabled': {
        borderColor: primary.disabledBorder,
      },
      '& $dropdownButtonCommon': {
        background: menuButton.primary.background,
        color: menuButton.primary.fontColor,
        '&:hover': {
          background: menuButton.primary.backgroundHover,
        },
        '&$disabled': {
          backgroundColor: primary.disabledBackground,
          borderColor: primary.disabledBorder,
          color: primary.disabledColor,
          cursor: 'not-allowed',
          '& svg': {
            fill: primary.disabledColor,
          },
        },
      },
      '& $divider': {
        background: menuButton.primary.dropdownToggleBorder,
      },
      '& svg': {
        fill: menuButton.primary.fontColor,
      },
    },
    '&$tertiary': {
      border: 0,
      '& $dropdownButtonCommon': {
        background: menuButton.tertiary.background,
        color: menuButton.tertiary.fontColor,
        '&:focus': {
          outline: 'none',
          boxShadow: 'none',
        },
        '&:hover': {
          background: menuButton.tertiary.backgroundHover,
          textDecoration: 'underline',
        },
        '&$disabled': {
          color: tertiary.disabledColor,
          textDecoration: 'none',
          cursor: 'not-allowed',
          '& svg': {
            fill: tertiary.disabledColor,
          },
        },
      },
      '& $divider': {
        background: menuButton.tertiary.dropdownToggleBorder,
        right: 28,
      },
      '& svg': {
        fill: menuButton.tertiary.fontColor,
      },
      '& + $dropdownMenu': {
        borderRadius: menuButton.dropdownRadius,
        '& $dropdownMenuItem:first-child': {
          borderRadius: [menuButton.dropdownRadius, menuButton.dropdownRadius, 0, 0],
        },
      },
    },
    '&$small': {
      height: 30,
      '&$isOpen': {
        borderRadius: [menuButton.small.borderRadius, menuButton.small.borderRadius, 0, 0],
        '& $dropdownButton': {
          borderRadius: [menuButton.small.borderRadius, 0, 0, 0],
        },
        '& $dropdownToggle': {
          borderRadius: [0, menuButton.small.borderRadius, 0, 0],
        },
      },
      '& + $dropdownMenu': {
        top: 30,
      },
      '& $dropdownButtonCommon': {
        fontSize: menuButton.small.fontSize,
        height: 28,
      },
      '& $dropdownButton': {
        borderRadius: [menuButton.small.borderRadius, 0, 0, menuButton.small.borderRadius],
        padding: [0, 16],
      },
      '& $dropdownToggle': {
        borderRadius: [0, menuButton.small.borderRadius, menuButton.small.borderRadius, 0],
        padding: [0, 14, 0, 10],
      },
      '& $divider': {
        right: 40,
      },
    },
    '&$extraSmall': {
      height: 22,
      '&$isOpen': {
        borderRadius: [
          menuButton.extraSmall.borderRadius,
          menuButton.extraSmall.borderRadius,
          0,
          0,
        ],
        '& $dropdownButton': {
          borderRadius: [menuButton.extraSmall.borderRadius, 0, 0, 0],
        },
        '& $dropdownToggle': {
          borderRadius: [0, menuButton.extraSmall.borderRadius, 0, 0],
        },
      },
      '& + $dropdownMenu': {
        top: 22,
      },
      '& $dropdownButtonCommon': {
        fontSize: menuButton.extraSmall.fontSize,
        height: 20,
      },
      '& $dropdownButton': {
        borderRadius: [
          menuButton.extraSmall.borderRadius,
          0,
          0,
          menuButton.extraSmall.borderRadius,
        ],
        padding: [0, 16],
      },
      '& $dropdownToggle': {
        borderRadius: [
          0,
          menuButton.extraSmall.borderRadius,
          menuButton.extraSmall.borderRadius,
          0,
        ],
        padding: [0, 16, 0, 8],
      },
      '& $divider': {
        height: 16,
        right: 40,
      },
    },
  },
  dropdownButtonCommon: {
    fontFamily: menuButton.fontFamily,
    fontSize: menuButton.fontSize,
    color: menuButton.fontColor,
    background: menuButton.background,
    border: 0,
    cursor: 'pointer',
    height: 38,
    '&:focus': {
      ...(primary.outlineReplacement
        ? { outline: 'none', boxShadow: primary.outlineReplacement }
        : undefined),
    },
    '&:hover': {
      background: menuButton.backgroundHover,
    },
    '& svg': {
      fill: menuButton.fontColor,
    },
    '&$disabled': {
      backgroundColor: secondary.disabledBackground,
      borderColor: secondary.disabledBorder,
      color: secondary.disabledColor,
      cursor: 'not-allowed',
      '& svg': {
        fill: secondary.disabledColor,
      },
    },
  },
  isOpen: {
    borderRadius: [menuButton.borderRadius, menuButton.borderRadius, 0, 0],
    '& $dropdownButton': {
      borderRadius: [menuButton.borderRadius, 0, 0, 0],
    },
    '& $dropdownToggle': {
      borderRadius: [0, menuButton.borderRadius, 0, 0],
    },
  },
  dropdownButton: {
    borderRadius: [menuButton.borderRadius, 0, 0, menuButton.borderRadius],
    padding: [0, 16, 0, 24],
  },
  dropdownToggle: {
    borderRadius: [0, menuButton.borderRadius, menuButton.borderRadius, 0],
    padding: [0, 20, 0, 12],
  },
  divider: {
    height: 20,
    width: 1,
    position: 'absolute',
    right: 48,
    backgroundColor: menuButton.dropdownToggleBorder,
  },
  dropdownMenu: {
    display: 'block',
    width: '100%',
    position: 'absolute',
    top: 40,
    zIndex: 1,
    left: 0,
    padding: 0,
    margin: 0,
    listStyle: 'none',
    border: `1px solid ${menuButton.borderColor}`,
    borderRadius: [0, 0, menuButton.dropdownRadius, menuButton.dropdownRadius],
    background: menuButton.dropdownBackground,
  },
  dropdownMenuItem: {
    '&:hover': {
      backgroundColor: menuButton.dropdownMenuHover,
    },
    '&:last-child': {
      borderRadius: [0, 0, menuButton.dropdownRadius, menuButton.dropdownRadius],
    },
  },
  dropdownAction: {
    border: 'none',
    cursor: 'pointer',
    background: 'transparent',
    padding: [5, 14],
    textAlign: 'left',
    width: '100%',
    fontSize: menuButton.fontSize,
    fontFamily: menuButton.fontFamily,
    lineHeight: menuButton.lineHeight,
    ...(menuButton.dropdownActionColor ? { color: menuButton.dropdownActionColor } : undefined),
  },
  disabled: {},
  primary: {},
  secondary: {},
  normal: {},
  tertiary: {},
  small: {},
  extraSmall: {},
  dotIcon: {
    padding: 3,
    cursor: 'pointer',
    '&$disabled': {
      cursor: 'not-allowed',
    },
  },
});

export default createUseStyles(styles);
